import { useState } from "react";
import { useMyActivity } from "../../controllers/useMyActivity";

type Tab = "issues" | "mentioned" | "myBlog" | "chats" | "notifications";

const TAB_LABEL: Record<Tab, string> = {
  issues: "내 이슈",
  mentioned: "멘션된 블로그",
  myBlog: "내가 만든 블로그의 활동",
  chats: "내 채팅",
  notifications: "알림",
};

// Account-wide "내 활동" surface: cross-project issues, blog mentions, activity on my own posts,
// my chat channels, and the notification feed (unread count, mark read / mark all read).
export function MyActivityView() {
  const my = useMyActivity();
  const [tab, setTab] = useState<Tab>("issues");

  return (
    <div className="detail">
      <div className="detail-name"><h1>내 활동</h1></div>
      <p className="meta">프로젝트 전반의 내 이슈·블로그·채팅 활동과 알림을 모아 봅니다.</p>

      {my.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {my.error}</div>}

      <div className="tabs">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
          <button key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {TAB_LABEL[t]}
            {t === "notifications" && my.unreadCount > 0 && ` · ${my.unreadCount}`}
          </button>
        ))}
      </div>

      {my.loading ? (
        <div className="center">불러오는 중…</div>
      ) : tab === "issues" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {my.activity.myIssues.map((i) => (
            <div key={i.id} className="screen-row">
              <span className="screen-id">{i.key}</span>
              <span>{i.summary}</span>
            </div>
          ))}
          {my.activity.myIssues.length === 0 && <div className="sdui-list-empty">해당 이슈가 없습니다.</div>}
        </div>
      ) : tab === "mentioned" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h4>멘션된 게시글</h4>
          {my.activity.mentionedPosts.map((p) => (
            <div key={p.id} className="screen-row">{p.title}</div>
          ))}
          {my.activity.mentionedPosts.length === 0 && <div className="sdui-list-empty">없습니다.</div>}
          <h4 style={{ marginTop: 12 }}>멘션된 댓글</h4>
          {my.activity.mentionedInComments.map((c) => (
            <div key={c.id} className="screen-row">{c.body}</div>
          ))}
          {my.activity.mentionedInComments.length === 0 && <div className="sdui-list-empty">없습니다.</div>}
        </div>
      ) : tab === "myBlog" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h4>내가 작성한 글</h4>
          {my.activity.myPosts.map((p) => (
            <div key={p.id} className="screen-row">{p.title}</div>
          ))}
          {my.activity.myPosts.length === 0 && <div className="sdui-list-empty">없습니다.</div>}
          <h4 style={{ marginTop: 12 }}>내 글에 달린 활동</h4>
          {my.activity.activityOnMyPosts.map((c) => (
            <div key={c.id} className="screen-row">{c.body}</div>
          ))}
          {my.activity.activityOnMyPosts.length === 0 && <div className="sdui-list-empty">없습니다.</div>}
        </div>
      ) : tab === "chats" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {my.activity.myChats.map((c) => (
            <div key={c.id} className="screen-row">
              <span className="tag">{c.type === "GROUP" ? "그룹" : "DM"}</span>
              <span>{c.name ?? "1:1 대화"}</span>
            </div>
          ))}
          {my.activity.myChats.length === 0 && <div className="sdui-list-empty">참여 중인 채널이 없습니다.</div>}
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button className="btn ghost" style={{ width: "auto" }} onClick={() => void my.markAllRead()}>모두 읽음 처리</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {my.notifications.map((n) => (
              <div key={n.id} className="screen-row" style={{ justifyContent: "space-between", opacity: n.read ? 0.6 : 1 }}>
                <span>{n.title || n.type}</span>
                {!n.read && (
                  <button className="sdui-button sdui-button-secondary" onClick={() => void my.markRead(n.id)}>읽음</button>
                )}
              </div>
            ))}
            {my.notifications.length === 0 && <div className="sdui-list-empty">알림이 없습니다.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
